//  MUI
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import Typography from '@mui/material/Typography';
import { a2aTransfersSelectors } from 'app-components/flux/a2aTransfers';
import { accountsActions, accountsTypes } from 'app-components/flux/accounts/index';
//  DATA
import type { institutionLoginsTypes } from 'app-components/flux/institution-logins';
import { preferencesActions, preferencesSelectors } from 'app-components/flux/preferences';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { useFetchOnce } from 'app-components/hooks/utils';
import { showInitiateTransferDialog, showMicrodespositDialog } from 'components/Dialogs/A2A/actions';
import type { onboardA2ADialogIndex } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
import { onboardA2ADialogIndexEnums } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
//  CUSTOM COMPONENTS
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import QButton from 'components/QButton';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';
import type { FundingAccount } from 'data/fundingAccounts/types';
import * as fundingAccountUtils from 'data/fundingAccounts/utils';
import { useAppSelector } from 'hooks/redux';
import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
//  MAIN
import { Map as ImmutableMap } from 'immutable';
import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  transferWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    marginBottom: -10,
    marginTop: 15,
    justifyContent: 'flex-end',
  },
  icon: {
    fill: theme.palette.primary.main,
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  titleIcon: {
    marginRight: 8,
    color: theme.palette.info.main,
    height: 24,
    width: 24,
  },
  dismissButton: {
    marginRight: 16,
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  wrapper: {
    width: '100%',
    height: 78,
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.palette.border.main,
    borderStyle: 'solid',
    justifyContent: 'space-between',
    padding: 16,
    '&.error': {
      borderColor: theme.palette.warning.main,
      backgroundColor: theme.palette.background.paper,
    },
  },
  leftText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: 48,
    color: theme.palette.text.primary,
  },
  bold: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
}));
type A2AEntryButtonProps = {
  institutionLogin: institutionLoginsTypes.InstitutionLogin;
  account: accountsTypes.Account;
  hideOnVerified?: boolean;
  onlyShowVerified?: boolean;
  disableDefaultStyling?: boolean;
};
function A2AEntryButton(props: A2AEntryButtonProps) {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  useFetchOnce(profileActions.getProfile, profileSelectors);
  const a2a = useAppSelector(
    (state) => preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset' }).a2a,
  );
  const { institutionLogin, account, hideOnVerified, onlyShowVerified, disableDefaultStyling } = props;
  const shouldHideA2ASetupBanner = (account && account.isA2ABannerDismissed) || (a2a && a2a.hideAllTransactionBanners);
  const kickoffA2AFlow = useKickoffA2AFlow();
  const isMicrodepositEligible = useAppSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForMicrodeposits(state, account),
  );
  const isMoveMoneyEligible = useAppSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForCashTransfer(state, account),
  );
  const { isEligible } = useAppSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForTransfers(state, account),
  );
  const fundingAccountsByAccountId = useAppSelector(fundingAccountSelectors.getAllFundingAccountsByAccountId);
  const a2aTransferEnabled = useAppSelector(a2aTransfersSelectors.a2aTransferEnabled);
  const myFundingAccount: FundingAccount | undefined = fundingAccountsByAccountId.get(account?.id || '');
  const depositOnly = fundingAccountUtils.isAccountA2aDepositOnly(account); // Show unique text for deposit only
  const accountStatus = {
    eligible: {
      enableTransferFunctions: a2aTransferEnabled && isEligible && !onlyShowVerified,
    },
    depositPending: {
      enableTransferFunctions: a2aTransferEnabled && isMicrodepositEligible?.isEligible && !onlyShowVerified,
      showFailureChipAndEnableFunctions: a2aTransferEnabled && isMicrodepositEligible?.isFailure && !onlyShowVerified,
    },
    verified: {
      enableTransferFunctions: a2aTransferEnabled && isMoveMoneyEligible?.isEligible && !hideOnVerified,
    },
  };

  const dismissButtonClick = () => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'close-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Dismiss Transfers',
              content:
                'You won’t see this again for this account. To set up transfers for this or other accounts in the future, go to Settings > Accounts.',
              confirmLabel: 'Dismiss',
              denyLabel: 'Cancel',
              size: 'xs',
              tertiaryLabelVariant: 'text',
              tertiaryLabel: 'Dismiss for all accounts',
              tertiaryAction: () => {
                dispatch(
                  preferencesActions.setPreference({
                    section: 'shared',
                    group: 'dataset',
                    preference: { a2a: { hideAllTransactionBanners: true } },
                  }),
                );
              },
              confirmAction: () =>
                dispatch(
                  accountsActions.updateAccount(
                    accountsTypes.mkUpdateAccount(account?.type, { id: account?.id, isA2ABannerDismissed: true }),
                    {
                      undo: { userMessage: 'Banner dismissed' },
                    },
                  ),
                ),
            }),
          ),
        }),
      ),
    );
  };
  const buttonClick = () => {
    if (accountStatus.eligible.enableTransferFunctions) {
      kickoffA2AFlow({
        initiator: 'transactions',
        startTabIndex: onboardA2ADialogIndexEnums.ACCT_INFO as onboardA2ADialogIndex,
        initialInstitutionId: institutionLogin?.institutionId || '',
        initialAccountId: account?.id || '',
      });
    } else if (
      accountStatus.depositPending.enableTransferFunctions ||
      accountStatus.depositPending.showFailureChipAndEnableFunctions
    ) {
      dispatch(
        showMicrodespositDialog({
          fundingAccountId: myFundingAccount?.id,
          hideAccountPicker: true,
          initiator: 'transactions',
        }),
      );
    } else if (accountStatus.verified.enableTransferFunctions) {
      dispatch(
        showInitiateTransferDialog({
          initiator: 'transactions',
          transferOptions: {
            fromAccount: account,
          },
        }),
      );
    }
  };
  return (
    a2aTransferEnabled &&
    account && (
      <>
        {(accountStatus.eligible.enableTransferFunctions || accountStatus.depositPending.enableTransferFunctions) &&
          !shouldHideA2ASetupBanner && (
            <div className={disableDefaultStyling ? '' : classes.wrapper}>
              <div className={classes.titleRow}>
                <CompareArrowsRoundedIcon className={classes.titleIcon} />
                <div className={classes.leftText}>
                  <Typography variant='bodyMedium' className={classes.bold}>
                    {accountStatus.eligible.enableTransferFunctions && 'Move money directly in Simplifi'}
                    {accountStatus.depositPending.enableTransferFunctions && 'Verify this account for transfers'}
                  </Typography>
                  <Typography variant='paragraphRegular'>
                    {accountStatus.eligible.enableTransferFunctions &&
                      depositOnly &&
                      'Set up this account now to start receiving funds!'}
                    {accountStatus.eligible.enableTransferFunctions &&
                      !depositOnly &&
                      'Start transferring funds with this account now.'}
                    {accountStatus.depositPending.enableTransferFunctions &&
                      'Confirm the two small deposit amounts we recently sent to this account.'}
                  </Typography>
                </div>
              </div>
              <div className={classes.buttonWrapper}>
                <QButton
                  className={classes.dismissButton}
                  id='a2a-dismiss-button'
                  onClick={dismissButtonClick}
                  variant='text'
                >
                  Dismiss
                </QButton>
                <QButton
                  id='a2a-begin-button'
                  onClick={buttonClick}
                  disabled={
                    !accountStatus.eligible.enableTransferFunctions &&
                    !accountStatus.depositPending.enableTransferFunctions &&
                    !accountStatus.verified.enableTransferFunctions
                  }
                  variant={accountStatus.depositPending.enableTransferFunctions ? 'contained' : 'outlined'}
                >
                  {accountStatus.eligible.enableTransferFunctions && 'Set Up Transfers'}
                  {accountStatus.depositPending.enableTransferFunctions && 'Verify'}
                </QButton>
              </div>
            </div>
          )}
        {accountStatus.depositPending.showFailureChipAndEnableFunctions && (
          <div className={cx(disableDefaultStyling ? '' : classes.wrapper, 'error')}>
            <div className={classes.leftText}>
              <Typography variant='bodyMedium' className={classes.bold}>
                Account verification failed
              </Typography>
              <Typography variant='paragraphRegular'>
                We had trouble delivering verification deposits to this account.
              </Typography>
            </div>
            <QButton id='a2a-begin-button' onClick={buttonClick} variant='contained'>
              View
            </QButton>
          </div>
        )}
        {accountStatus.verified.enableTransferFunctions && (
          <div className={disableDefaultStyling ? '' : classes.transferWrapper}>
            <QButton
              id='a2a-begin-button'
              onClick={buttonClick}
              startIcon={<CompareArrowsRoundedIcon className={classes.icon} />}
              variant='outlined'
            >
              Transfer Money
            </QButton>
          </div>
        )}
      </>
    )
  );
}
export default A2AEntryButton;
